import { Component, ErrorInfo, ReactNode } from 'react';
import { captureException } from '@/posthog-analytics/posthog';
import { ErrorFallback } from './ErrorFallback';

interface AppErrorBoundaryProps {
  children: ReactNode;
  /**
   * Names the surface in error tracking. React unmounts the whole tree on a render
   * crash, so without this every crash arrives as an anonymous stack and a meeting
   * failure is indistinguishable from an ATS one.
   */
  boundary: string;
  fallback?: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

/**
 * A render crash used to reach nothing: there was no boundary above the router,
 * so the tree unmounted silently and error tracking never saw it.
 */
export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureException(error, {
      boundary: this.props.boundary,
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return this.props.fallback ?? <ErrorFallback />;
  }
}
